"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { deleteAppointment } from "@/lib/appointments-api";
import type { Appointment } from "@/types";

interface DeleteAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Single delete: the dialog performs the DELETE and the toasts itself. */
  appointment?: Pick<Appointment, "id" | "display_title"> | null;
  /** Bulk delete: the caller performs the deletes; the dialog only confirms. */
  count?: number;
  onConfirm?: () => Promise<void> | void;
  /** Fired after a successful single delete. */
  onDeleted?: () => void;
}

/**
 * 86-02: THE delete confirmation for appointments — the sheet, the lead tabs
 * and the list page all mount this one, so the wording, the destructive
 * styling and the "cannot be undone" body never drift again (the audit found
 * two AlertDialogs and a bare `confirm()`).
 */
export function DeleteAppointmentDialog({
  open,
  onOpenChange,
  appointment,
  count,
  onConfirm,
  onDeleted,
}: DeleteAppointmentDialogProps) {
  const t = useTranslations("appointments.deleteDialog");
  const tc = useTranslations("common");
  const [pending, setPending] = useState(false);
  const many = (count ?? 0) > 1;

  const run = async () => {
    setPending(true);
    try {
      if (appointment) {
        await deleteAppointment(appointment.id);
        toast.success(t("deleted"));
        onOpenChange(false);
        onDeleted?.();
      } else {
        await onConfirm?.();
        onOpenChange(false);
      }
    } catch {
      toast.error(t("deleteFailed"));
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {many ? t("titleMany", { count: count ?? 0 }) : t("title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {many ? t("bodyMany", { count: count ?? 0 }) : t("body")}
            {appointment?.display_title ? (
              <span className="mt-2 block font-medium text-foreground">
                {appointment.display_title}
              </span>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{tc("cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            className="bg-destructive text-white hover:bg-destructive/90"
            onClick={(e) => {
              e.preventDefault();
              void run();
            }}
          >
            {t("confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
